// packages/frontend/src/components/FileEditorModal.tsx
// ✅ FIXED: Async state + Monaco onChange race condition solved
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { X, Save, RefreshCw, FileText, Clock, HardDrive, Maximize2, Minimize2, Eye, EyeOff } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useToastStore } from '../stores/toastStore';
import { useAuthStore } from '../stores/authStore';
import { formatFileSize } from '../utils/api';

interface FileEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
  fileName: string;
  onSave?: () => void;
}

interface FileInfo {
  id: string;
  name: string;
  path: string;
  size: number;
  mimeType: string;
  isPublic: boolean;
  isEditable: boolean;
  canEdit: boolean;
  editSizeLimit: number;
}

import { WORKER_URL } from '../config';

const getLanguageFromFileName = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const languageMap: Record<string, string> = {
    'js': 'javascript', 'jsx': 'javascript', 'ts': 'typescript', 'tsx': 'typescript',
    'html': 'html', 'htm': 'html', 'css': 'css', 'scss': 'scss', 'sass': 'scss', 'less': 'less',
    'json': 'json', 'xml': 'xml', 'php': 'php', 'py': 'python', 'rb': 'ruby', 'go': 'go',
    'java': 'java', 'cpp': 'cpp', 'c': 'c', 'h': 'c', 'hpp': 'cpp', 'cs': 'csharp',
    'sql': 'sql', 'sh': 'shell', 'bash': 'shell', 'zsh': 'shell', 'ps1': 'powershell',
    'yml': 'yaml', 'yaml': 'yaml', 'md': 'markdown', 'txt': 'plaintext', 'log': 'plaintext',
    'ini': 'ini', 'conf': 'plaintext', 'htaccess': 'apache', 'env': 'plaintext',
    'vue': 'vue', 'svelte': 'svelte', 'rs': 'rust', 'kt': 'kotlin', 'swift': 'swift'
  };
  return languageMap[ext || ''] || 'plaintext';
};

export const FileEditorModal: React.FC<FileEditorModalProps> = ({ isOpen, onClose, fileId, fileName, onSave }) => {
  // ✅ FIX #1: Add ref for IMMEDIATE synchronous content tracking
  const contentRef = useRef<string>('');
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const editorRef = useRef<any>(null);
  
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [keyboardSaveTriggered, setKeyboardSaveTriggered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { addToast } = useToastStore();
  const { token, csrfToken } = useAuthStore();

  const makeApiRequest = async (endpoint: string, options: RequestInit = {}) => {
    const url = `${WORKER_URL}${endpoint}`;
    const headers: HeadersInit = { 'Content-Type': 'application/json', ...options.headers };
    if (token) (headers as any)['Authorization'] = `Bearer ${token}`;
    if (csrfToken && options.method && !['GET', 'HEAD', 'OPTIONS'].includes(options.method.toUpperCase())) (headers as any)['X-CSRF-Token'] = csrfToken;
    const response = await fetch(url, { ...options, headers });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    return await response.json();
  };

  const loadFileContent = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!token) throw new Error('No authentication token found. Please log in again.');
      
      const infoResponse = await makeApiRequest(`/api/files/${fileId}/info`);
      if (!infoResponse.success) throw new Error(infoResponse.error || 'Failed to load file info');
      setFileInfo(infoResponse.data.file);
      
      const contentResponse = await makeApiRequest(`/api/files/${fileId}/content`);
      if (!contentResponse.success) throw new Error(contentResponse.error || 'Failed to load file content');
      
      const fileContent = contentResponse.data.content || '';
      
      // ✅ FIX: Initialize BOTH ref and state
      contentRef.current = fileContent;
      setContent(fileContent);
      setOriginalContent(fileContent);
      setHasChanges(false);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load file';
      setError(errorMessage);
      addToast({ type: 'error', message: `Failed to load file: ${errorMessage}` });
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIX #2: Save from ref (always up-to-date), not state
  const handleSave = async () => {
    if (isSaving) return;
    
    // Use ref for CURRENT content (not delayed state)
    const currentContent = contentRef.current;
    const lineCount = currentContent.split('\n').length;
    
    // Validate
    if (currentContent === originalContent) {
      addToast({ type: 'info', message: 'No changes to save' });
      return;
    }
    
    console.log(`💾 Saving ${lineCount} lines (${currentContent.length} chars)`);
    
    setIsSaving(true);
    setError(null);
    
    try {
      if (!token) throw new Error('No authentication token found. Please log in again.');
      
      const response = await makeApiRequest(`/api/files/${fileId}/content`, {
        method: 'PUT',
        body: JSON.stringify({ content: currentContent }), // ← Save from ref!
      });
      
      if (!response.success) throw new Error(response.error || 'Failed to save file');
      
      // Update all references after successful save
      setOriginalContent(currentContent);
      contentRef.current = currentContent;
      setContent(currentContent);
      setHasChanges(false);
      setLastSaved(new Date());
      
      addToast({ type: 'success', message: 'File saved successfully' });
      
      if (typeof (window as any).refreshStorageUsage === 'function') {
        (window as any).refreshStorageUsage();
      }
      
      onSave?.();
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save file';
      setError(errorMessage);
      addToast({ type: 'error', message: `Failed to save file: ${errorMessage}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmed = confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
    setTimeout(() => {
      setFileInfo(null);
      contentRef.current = '';
      setContent('');
      setOriginalContent('');
      setHasChanges(false);
      setIsFullscreen(false);
      setShowPreview(false);
      setLastSaved(null);
      setError(null);
      setKeyboardSaveTriggered(false);
    }, 300);
  };

  // ✅ FIX #3: Debounced onChange with immediate ref update
  const handleContentChange = useCallback((value: string | undefined) => {
    const newContent = value || '';
    
    // Update ref IMMEDIATELY (synchronous, no delay)
    contentRef.current = newContent;
    
    // Debounce state updates (reduce re-renders)
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    
    debounceTimerRef.current = setTimeout(() => {
      setContent(newContent);
      setHasChanges(newContent !== originalContent);
    }, 150); // Wait 150ms before updating UI state
  }, [originalContent]);

  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    editor.focus();

    try {
      if (editor.addCommand && monaco) {
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
          handleSave();
        });
      }
    } catch (error) {
      // Fallback to global keyboard handler
    }
  };

  useEffect(() => {
    if (isOpen && fileId) loadFileContent();
  }, [isOpen, fileId]);

  // ✅ FIX #4: Keyboard shortcuts properly bound
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        e.stopPropagation();
        setKeyboardSaveTriggered(true);
        setTimeout(() => setKeyboardSaveTriggered(false), 1000);
        handleSave(); // ← Now uses ref, always correct!
        return false;
      }
      
      if (e.key === 'F11') {
        e.preventDefault();
        e.stopPropagation();
        setIsFullscreen(!isFullscreen);
        return false;
      }
      
      if (e.key === 'Escape' && !hasChanges) {
        e.preventDefault();
        handleClose();
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, hasChanges, isFullscreen]);

  if (!isOpen) return null;

  const language = getLanguageFromFileName(fileName);

  return (
    <div className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm ${isFullscreen ? 'p-0' : 'p-4'}`}>
      <div className={`bg-dark-800 border border-dark-600 shadow-2xl flex flex-col ${
        isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-6xl h-[90vh] mx-auto mt-4 rounded-xl'
      }`}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-600 bg-dark-900/50">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-primary-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">{fileName}</h2>
              <div className="flex items-center gap-4 text-sm text-dark-400">
                {fileInfo && (
                  <>
                    <span className="flex items-center gap-1">
                      <HardDrive className="w-3 h-3" />
                      {formatFileSize(fileInfo.size)}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${fileInfo.isPublic ? 'bg-green-400' : 'bg-red-400'}`} />
                      {fileInfo.isPublic ? 'Public' : 'Private'}
                    </span>
                    <span className="text-xs text-dark-500">{language.toUpperCase()}</span>
                  </>
                )}
                {lastSaved && (
                  <span className="flex items-center gap-1 text-green-400">
                    <Clock className="w-3 h-3" />
                    Saved {lastSaved.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {(language === 'html' || language === 'markdown') && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`p-2 rounded-lg transition-colors ${
                  showPreview ? 'bg-primary-500 text-white' : 'text-dark-400 hover:text-white hover:bg-dark-700'
                }`}
                title="Toggle Preview"
              >
                {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
            
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
              title="Toggle Fullscreen (F11)"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="flex items-center gap-2 px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              title="Save (Ctrl+S)"
            >
              {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
            
            <button
              onClick={handleClose}
              className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
              title="Close (Esc)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {error && (
          <div className="px-4 py-2 bg-red-500/20 border-b border-red-500/20">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 flex overflow-hidden">
          <div className={`${showPreview ? 'w-1/2' : 'w-full'} flex flex-col`}>
            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-2" />
                  <p className="text-dark-400">Loading file...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <FileText className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-red-400 mb-2">Failed to load file</p>
                  <p className="text-dark-400 text-sm">{error}</p>
                  <button onClick={loadFileContent} className="mt-4 btn btn-primary btn-sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <Editor
                height="100%"
                language={language}
                value={content}
                onChange={handleContentChange}
                onMount={handleEditorDidMount}
                theme="vs-dark"
                options={{
                  fontSize: 14,
                  fontFamily: 'JetBrains Mono, Fira Code, Monaco, Consolas, monospace',
                  minimap: { enabled: !isFullscreen ? false : true },
                  wordWrap: 'on',
                  lineNumbers: 'on',
                  folding: true,
                  matchBrackets: 'always',
                  autoIndent: 'advanced',
                  formatOnPaste: true,
                  formatOnType: true,
                  tabSize: 2,
                  insertSpaces: true,
                  detectIndentation: true,
                  trimAutoWhitespace: true,
                  scrollBeyondLastLine: false,
                  smoothScrolling: true,
                  cursorSmoothCaretAnimation: 'on',
                  mouseWheelZoom: true,
                  contextmenu: true,
                  quickSuggestions: true,
                  suggestOnTriggerCharacters: true,
                  acceptSuggestionOnEnter: 'on',
                  wordBasedSuggestions: true
                }}
              />
            )}
          </div>
          
          {showPreview && (
            <div className="w-1/2 border-l border-dark-600 bg-white">
              <div className="h-full overflow-auto p-4">
                {language === 'html' ? (
                  <iframe srcDoc={content} className="w-full h-full border-0" title="HTML Preview" />
                ) : language === 'markdown' ? (
                  <div className="prose prose-sm max-w-none text-gray-800">
                    <ReactMarkdown>{content}</ReactMarkdown>
                  </div>
                ) : (
                  <pre className="whitespace-pre-wrap text-gray-800 font-mono text-sm">{content}</pre>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-dark-900 border-t border-dark-600 text-xs">
          <div className="flex items-center gap-4 text-dark-400">
            <span>Language: {language}</span>
            <span>Lines: {content.split('\n').length}</span>
            <span>Characters: {content.length}</span>
            {hasChanges && <span className="text-yellow-400">● Unsaved changes</span>}
          </div>
          
          <div className="flex items-center gap-4 text-dark-400">
            <span className={keyboardSaveTriggered ? 'text-green-400 transition-colors' : ''}>
              Ctrl+S to save {keyboardSaveTriggered ? '✓' : ''}
            </span>
            <span>F11 for fullscreen</span>
          </div>
        </div>
      </div>
    </div>
  );
};